/**
 * Normuren-instellingen — DTO's + Zod-schemas (batch-9-vervolg V1, PO #4).
 *
 * Maakt de kostprijs-normuren (werkdagen/jaar + declarabele uren/jaar)
 * instelbaar per jaar / BV / functie (employeeType) / regeling, bovenop de
 * bedrijfsbrede `CalculationParameter`-defaults. De resolver in de API kiest
 * de meest-specifieke actieve rij; ontbreekt een match dan gelden de globale
 * defaults. Bestaande calculaties blijven ongewijzigd (parameter-set-snapshot).
 *
 * Eén gedeeld request-schema voor API + form (CLAUDE.md: inline validatie).
 */

import { z } from 'zod'

/// De vier functies (employeeType) waarop een normuren-override kan scopen.
/// In sync met de Prisma-enum `EmployeeType`.
export const NORMUREN_EMPLOYEE_TYPES = [
  'ONDERHOUD',
  'STORING',
  'SOFTWARE',
  'PROJECT',
  'UNIVERSEEL',
  'KANTOOR',
] as const

export type NormurenEmployeeType = (typeof NORMUREN_EMPLOYEE_TYPES)[number]

export interface NormurenInstellingDTO {
  id: string
  jaar: number | null
  entityId: string | null
  entityNaam: string | null
  employeeType: NormurenEmployeeType | null
  regeling: string | null
  werkdagenJaar: number
  declarabeleUrenJaar: number | null
  notitie: string | null
  createdAt: string
  updatedAt: string
}

/// De effectieve normuren voor een gegeven scope, met de bron (override of
/// globale default), voor weergave in de vergelijk-/calculatie-UI.
export interface EffectieveNormurenDTO {
  werkdagenJaar: number
  declarabeleUrenJaar: number
  bron: 'OVERRIDE' | 'GLOBAAL'
  instellingId: string | null
}

const jaarSchema = z
  .number()
  .int()
  .min(2000)
  .max(2100)
  .nullable()
  .optional()

/// Gedeeld create/update-request-schema. Alle scope-velden optioneel/nullable
/// (null/weglaten = bedrijfsbreed op die as). `werkdagenJaar` verplicht en > 0;
/// `declarabeleUrenJaar` optioneel (null = val terug op de globale parameter).
export const upsertNormurenInstellingSchema = z.object({
  jaar: jaarSchema,
  entityId: z.string().min(1).nullable().optional(),
  employeeType: z.enum(NORMUREN_EMPLOYEE_TYPES).nullable().optional(),
  regeling: z.string().trim().max(120).nullable().optional(),
  werkdagenJaar: z.number().positive().max(366),
  declarabeleUrenJaar: z.number().positive().max(8760).nullable().optional(),
  notitie: z.string().trim().max(1000).nullable().optional(),
})

export type UpsertNormurenInstellingInput = z.infer<typeof upsertNormurenInstellingSchema>
